import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { appointmentsApi } from '@/api';
import { ApiError } from '@/api/client';
import { useAuth } from '@/context/AuthContext';
import type { Appointment } from '@/types';

/** Uses the existing owner-filtered agenda API; it does not broaden access. */
export default function PatientAppointments({ patientId, editable = false }: {
  patientId: string; editable?: boolean;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [retry, setRetry] = useState(0);
  const [cancelling, setCancelling] = useState<Appointment | null>(null);
  const [reason, setReason] = useState('');
  const [cancelError, setCancelError] = useState('');
  const [busy, setBusy] = useState(false);
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError('');
    appointmentsApi.list({ patient_id: patientId }).then((list) => {
      if (active) setAppointments(list);
    }).catch(() => {
      if (active) setError(t('errors.network'));
    }).finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [patientId, retry, t]);

  useEffect(() => {
    if (cancelling) dialog.current?.showModal();
  }, [cancelling]);

  const cancel = async () => {
    if (!cancelling || busy) return;
    setBusy(true);
    setCancelError('');
    try {
      await appointmentsApi.cancel(cancelling.id, reason);
      setAppointments((list) => list.map((item) => item.id === cancelling.id
        ? { ...item, status: 'CANCELLED' } : item));
      setCancelling(null);
    } catch (err) {
      setCancelError(err instanceof ApiError ? err.message : t('errors.generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section className="card" data-cy="patient-appointments">
      <div className="card-header"><h2>{t('patientHistory.appointments')}</h2></div>
      <p className="t-pequeno t-suave">{t('patientHistory.appointmentsScope')}</p>
      {loading ? <p>{t('common.loading')}</p> : error ? (
        <div role="alert" className="notice notice-error">
          {error} <button className="secondary is-pequeno" onClick={() => setRetry((n) => n + 1)}>{t('common.retry')}</button>
        </div>
      ) : appointments.length === 0 ? <p>{t('common.empty')}</p> : (
        <div className="table-wrapper">
          <table>
            <thead><tr>
              <th>{t('agenda.start')}</th><th>{t('agenda.end')}</th>
              <th>{t('agenda.modality')}</th><th>{t('agenda.status')}</th>
              {editable && <th>{t('common.actions')}</th>}
            </tr></thead>
            <tbody>{appointments.map((item) => (
              <tr key={item.id}>
                <td>{new Date(item.start_time).toLocaleString('es-PY')}</td>
                <td>{new Date(item.end_time).toLocaleTimeString('es-PY', { hour: '2-digit', minute: '2-digit' })}</td>
                <td>{t(`agenda.modalities.${item.modality}`)}</td>
                <td>{t(`agenda.statuses.${item.status}`)}</td>
                {editable && <td>{(user?.role === 'PSYCHOLOGIST' || user?.role === 'ADMIN') &&
                  item.psychologist_id === user.id && ['SCHEDULED', 'CONFIRMED'].includes(item.status) && (
                    <button className="link" onClick={() => {
                      setReason(''); setCancelError(''); setCancelling(item);
                    }}>{t('agenda.cancelAppointment')}</button>
                  )}</td>}
              </tr>
            ))}</tbody>
          </table>
        </div>
      )}
      {cancelling && (
        <dialog ref={dialog} className="modal" data-cy="patient-cancel-dialog"
          aria-labelledby="patient-cancel-title"
          onCancel={(event) => { if (busy) event.preventDefault(); else setCancelling(null); }}>
          <form onSubmit={(event) => { event.preventDefault(); void cancel(); }}>
            <h2 id="patient-cancel-title">{t('agenda.cancelAppointment')}</h2>
            <p>{new Date(cancelling.start_time).toLocaleString('es-PY')}</p>
            {cancelError && <div role="alert" className="notice notice-error">{cancelError}</div>}
            <div className="field">
              <label htmlFor="patient-cancel-reason">{t('agenda.cancelReason')}</label>
              <textarea id="patient-cancel-reason" rows={3} maxLength={500} value={reason}
                onChange={(event) => setReason(event.target.value)} disabled={busy}
                placeholder={t('agenda.cancelReasonPlaceholder')} data-cy="patient-cancel-reason" />
              <p className="t-pequeno t-suave">{t('agenda.cancelReasonHint')}</p>
            </div>
            <div className="button-row">
              <button type="button" className="secondary" disabled={busy} onClick={() => setCancelling(null)}>{t('common.back')}</button>
              <button type="submit" className="danger" disabled={busy} data-cy="patient-cancel-confirm">
                {busy ? t('common.loading') : t('agenda.cancelAppointment')}
              </button>
            </div>
          </form>
        </dialog>
      )}
    </section>
  );
}
